/**
 * Duration formatting for the platform screens. An instant renders through
 * `formatInstant` (`lib/time/displayTime.ts`) in the reader's display zone.
 *
 * A value that cannot be parsed renders as an em dash rather than as
 * "Invalid Date", which is a string users reasonably read as a status.
 */

export const EMPTY_CELL = "—";

function parse(iso: string | null | undefined): Date | null {
  if (!iso) return null;
  const date = new Date(iso);
  return Number.isNaN(date.getTime()) ? null : date;
}

/**
 * "4.1 s", "2 min 6 s" — how long a step took.
 *
 * Seconds keep one decimal below a minute because provisioning steps differ by
 * fractions of a second and a rounded "4 s" beside another "4 s" hides which
 * one is the slow one.
 */
export function formatDuration(startedAt: string | null, finishedAt: string | null): string | null {
  const start = parse(startedAt);
  const end = parse(finishedAt);
  if (!start || !end) return null;
  const ms = end.getTime() - start.getTime();
  if (ms < 0) return null;
  if (ms < 60_000) return `${(ms / 1000).toFixed(1)} s`;
  const minutes = Math.floor(ms / 60_000);
  const seconds = Math.round((ms % 60_000) / 1000);
  return `${minutes} min ${seconds} s`;
}

/** "14 min ago", for a health tile's last-seen line. */
export function formatAgo(iso: string | null | undefined, now = Date.now()): string {
  const date = parse(iso);
  if (!date) return "never";
  const seconds = Math.max(0, Math.round((now - date.getTime()) / 1000));
  if (seconds < 60) return `${seconds} s ago`;
  const minutes = Math.round(seconds / 60);
  if (minutes < 60) return `${minutes} min ago`;
  const hours = Math.round(minutes / 60);
  if (hours < 48) return `${hours} h ago`;
  return `${Math.round(hours / 24)} days ago`;
}

/**
 * A count this deployment could not read renders as "unknown", never as 0
 * (`shared/types/health.ts`): on a dashboard a wrong zero reads as a healthy
 * idle queue, which is the one reading that stops an operator looking further.
 */
export function formatCount(value: number | null | undefined): string {
  return value === null || value === undefined ? "unknown" : String(value);
}
